/**
 * Builds a full authenticated URL from a relative API path.
 *
 * When the BIM+ API returns thumbnail paths with addThumbnailUrl=true, the path is
 * relative, e.g. "/jgaba-nemetschek-com/attachments/{id}/download".
 * This utility combines it with the API base URL and appends the api-token query param.
 *
 * @param baseUrl  - The API base URL, e.g. "https://api-dev.bimplus.net/v2/"
 * @param relativePath - The relative path, e.g. "/team/attachments/{id}/download"
 * @param token - Optional access token to append as ?api-token={token}
 * @returns Full authenticated URL string
 */
export function buildAuthenticatedUrl(
  baseUrl: string,
  relativePath: string,
  token: string | null
): string {
  const cleanPath = relativePath.replace(/^\//, '');
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const url = new URL(cleanPath, base);
  if (token) {
    url.searchParams.set('api-token', token);
  }
  return url.toString();
}

/**
 * Removes a specified query parameter from a URL string.
 *
 * @param url - The URL string to process. Can be a relative or absolute URL.
 * @param param - The name of the query parameter to remove.
 * @returns The URL without the specified parameter, or `null` if the input URL is undefined.
 *
 */
export function removeParamFromUrl(url: string | undefined, param: string): string | null {
  if (!url) {
    return null;
  }
  try {
    const urlObj = new URL(url, globalThis.location.origin);
    urlObj.searchParams.delete(param);
    return urlObj.pathname + urlObj.search;
  } catch {
    return url;
  }
}

/**
 * Rewrites legacy hash-based URLs like `/app/#/route` into clean path URLs before Angular bootstraps.
 *
 * This helps preview environments that can serve the app shell from a subpath but do not support
 * SPA rewrites for nested routes.
 *
 * @returns `true` when a legacy hash URL was rewritten, otherwise `false`.
 */
export function rewriteLegacyHashUrl(): boolean {
  const { hash, origin, search } = globalThis.location;

  if (!hash.startsWith('#/')) {
    return false;
  }

  const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
  const basePath = new URL(baseHref, origin).pathname.replace(/\/$/, '');
  const [routePath, routeSearch = ''] = hash.slice(1).split('?');
  const nextUrl = new URL(`${origin}${basePath}${routePath}`);
  const searchParams = new URLSearchParams(search);
  const routeSearchParams = new URLSearchParams(routeSearch);

  routeSearchParams.forEach((value, key) => searchParams.set(key, value));

  nextUrl.search = searchParams.toString();

  globalThis.history.replaceState(globalThis.history.state, '', nextUrl.toString());

  return true;
}
